import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useApiClient } from '../api/apiContext';
import { readConfig } from '../config/env';
import { connectStomp, socketUrlFrom } from './stomp';

/**
 * Keeps the branch inbox current while somebody is looking at it (FR-1003).
 *
 * Unlike the day-board, the payload is the message itself rather than a version number. The
 * argument that keeps patient names off the board's channel does not apply here: this destination
 * carries one branch's patient conversations to the people whose job is to read them, and a
 * refetch per message would put a request on the server for every sentence typed in the clinic.
 *
 * The destination is still clinic-scoped and still authorised by the server against the
 * subscriber's token — the same rule the board uses, and the reason the desk can use a topic where
 * the patient app and the doctor app cannot.
 */
export function useChatStream(
  clinicId: string | null,
  branchId: string | null,
  onMessage?: (message: ChatStreamMessage) => void,
): boolean {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!clinicId || !branchId) {
      setLive(false);
      return undefined;
    }

    const disconnect = connectStomp({
      url: socketUrlFrom(readConfig().apiBaseUrl),
      accessToken: () => client.getTokens()?.accessToken ?? null,
      onStateChange: setLive,
      subscriptions: [
        {
          destination: `/topic/clinic/${clinicId}/branch/${branchId}/chat`,
          onMessage: (body) => {
            const message = body as ChatStreamMessage;
            // Every inbox view for this branch, whatever status filter it was fetched under: a
            // message arriving into a closed thread reopens it, and the live list has to grow a
            // row it was not previously showing.
            void queryClient.invalidateQueries({
              queryKey: ['clinics', clinicId, 'chat', 'inbox', branchId],
            });
            if (message?.threadId) {
              void queryClient.invalidateQueries({
                queryKey: ['clinics', clinicId, 'chat', 'threads', message.threadId],
              });
            }
            onMessage?.(message);
          },
        },
      ],
    });

    return () => {
      disconnect();
      setLive(false);
    };
    // `onMessage` is deliberately absent: it is an inline callback in every caller, and including
    // it would tear down and rebuild the socket on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, queryClient, clinicId, branchId]);

  return live;
}

export interface ChatStreamMessage {
  readonly clinicId: string;
  readonly threadId: string;
  readonly threadKind: string;
  readonly branchId: string;
  readonly messageId: string;
  readonly senderKind: string;
  readonly senderName: string;
  readonly body: string;
  readonly hasAttachment: boolean;
  readonly createdAt: string;
  readonly fromPatient: boolean;
}
